import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Printer, Scale, Trash2, CheckCircle2, ChevronDown, ChevronUp, FileDown, ListTree, ScanText } from "lucide-react";
import { wmsApi, openWmsPdf, fmtKg, fmtKg2, fmtDate, fmtEur, fmtDateTime } from "./wmsApi";
import { WButton, WInput, WField, WCard, WBadge, WModal, WToggle } from "./WmsUi";
import WmsPieceCard from "./WmsPieceCard";
import WmsCamera from "./WmsCamera";
import { PresetChip } from "./WmsArticles";

const EMPTY_LOT = {
  product_id: "", supplier_product_code: "", supplier_description: "", packaging: "appeso", um: "kg",
  lot_supplier: "", expiry_date: "", purchase_price: "", price_um: "kg",
  expected_kg: "", expected_units: "", brand_default: "", photos: [],
  origin: { birth: "", rearing: "", slaughter: "", slaughter_code: "", cutting: "", cutting_code: "" },
};

export default function WmsReceiptDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [receipt, setReceipt] = useState(null);
  const [boot, setBoot] = useState(null);
  const [showLotForm, setShowLotForm] = useState(false);
  const [lotForm, setLotForm] = useState(EMPTY_LOT);
  const [prodSearch, setProdSearch] = useState("");
  const [showOrigin, setShowOrigin] = useState(false);
  const [openLots, setOpenLots] = useState({});
  const [scanPiece, setScanPiece] = useState(null);
  const [presets, setPresets] = useState([]);
  const [labelScanOpen, setLabelScanOpen] = useState(false);
  const [movLot, setMovLot] = useState(null);
  const [movs, setMovs] = useState([]);

  const load = useCallback(() => {
    wmsApi.get(`/wms/receipts/${id}`).then((r) => setReceipt(r.data)).catch(() => toast.error("Carico non trovato"));
  }, [id]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { wmsApi.get("/wms/bootstrap").then((r) => setBoot(r.data)).catch(() => {}); }, []);

  const products = useMemo(() => {
    if (!boot) return [];
    const s = prodSearch.toLowerCase();
    if (!s) return [];
    return boot.products.filter((p) => `${p.code} ${p.name} ${p.category_name}`.toLowerCase().includes(s)).slice(0, 8);
  }, [boot, prodSearch]);

  const selectedProduct = boot?.products.find((p) => p.id === lotForm.product_id);

  useEffect(() => {
    if (!lotForm.product_id) { setPresets([]); return; }
    wmsApi.get(`/wms/products/${lotForm.product_id}/origin-presets`).then((r) => setPresets(r.data.presets || [])).catch(() => setPresets([]));
  }, [lotForm.product_id]);

  const applyPreset = (p) => {
    setLotForm((f) => ({ ...f, origin: { birth: p.birth || "", rearing: p.rearing || "", slaughter: p.slaughter || "", slaughter_code: p.slaughter_code || "", cutting: p.cutting || "", cutting_code: p.cutting_code || "" } }));
    setShowOrigin(true);
  };

  const analyzeLabel = async (blobs) => {
    try {
      const fd = new FormData();
      fd.append("file", blobs[0], "label.jpg");
      toast.info("L'AI sta leggendo l'etichetta…");
      const r = await wmsApi.post("/wms/ai/scan-label", fd);
      const d = r.data;
      setLotForm((f) => ({
        ...f,
        lot_supplier: f.lot_supplier || d.lot || "",
        expiry_date: f.expiry_date || d.expiry_date || "",
        origin: Object.values(f.origin).some((v) => v) ? f.origin : d.origin,
        photos: [...(f.photos || []), d.photo_path],
      }));
      setShowOrigin(true);
      toast.success("Dati etichetta compilati — foto collegata alla riga");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore lettura etichetta");
    }
  };

  const openMovs = async (lot) => {
    setMovLot(lot);
    try {
      const r = await wmsApi.get("/wms/itrace-movements", { params: { lot_id: lot.id } });
      setMovs(r.data);
    } catch { setMovs([]); }
  };

  const downloadReport = async () => {
    try {
      const r = await wmsApi.get(`/wms/receipts/${id}/report.xlsx`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `scarico_${receipt.number}.xlsx`; a.click();
    } catch { toast.error("Errore export resoconto"); }
  };

  const saveLot = async () => {
    if (!lotForm.product_id) { toast.error("Seleziona un articolo"); return; }
    try {
      const payload = {
        ...lotForm,
        purchase_price: lotForm.purchase_price === "" ? null : parseFloat(lotForm.purchase_price),
        expected_kg: lotForm.expected_kg === "" ? null : parseFloat(lotForm.expected_kg),
        expected_units: lotForm.expected_units === "" ? null : parseFloat(lotForm.expected_units),
      };
      await wmsApi.post(`/wms/receipts/${id}/lots`, payload);
      toast.success("Riga articolo aggiunta");
      setShowLotForm(false);
      setLotForm(EMPTY_LOT);
      setProdSearch("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore salvataggio riga");
    }
  };

  const confirm = async () => {
    try {
      await wmsApi.post(`/wms/receipts/${id}/confirm`);
      toast.success("Carico confermato — fornitore e prezzi memorizzati");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore conferma");
    }
  };

  const deleteLot = async (lid) => {
    if (!window.confirm("Eliminare questa riga articolo?")) return;
    try { await wmsApi.delete(`/wms/lots/${lid}`); load(); } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  if (!receipt) return <p className="text-zinc-500 py-8">Caricamento...</p>;

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="font-['Chivo'] font-black text-2xl md:text-3xl tracking-tighter">{receipt.number}</h1>
            <WBadge tone={receipt.status === "aperto" ? "orange" : "green"}>{receipt.status}</WBadge>
            {receipt.esaurito && <WBadge tone="green">TUTTO SCARICATO ✓</WBadge>}
          </div>
          <p className="font-bold text-zinc-800 mt-1">{receipt.supplier_name}</p>
          <p className="text-sm text-zinc-500">DDT {receipt.ddt_number || "—"} del {fmtDate(receipt.ddt_date)}</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <WButton onClick={downloadReport} disabled={!receipt.pieces_count} data-testid="report-btn"><FileDown className="w-5 h-5" /> Resoconto</WButton>
          <WButton variant="primary" onClick={() => setShowLotForm(true)} data-testid="add-lot-btn"><Plus className="w-5 h-5" /> Articolo</WButton>
          {receipt.status === "aperto" && (
            <WButton variant="success" onClick={confirm} data-testid="confirm-receipt-btn"><CheckCircle2 className="w-5 h-5" /> Conferma</WButton>
          )}
        </div>
      </div>

      {/* CONTATORI CARICO/SCARICO */}
      <div className="grid grid-cols-3 gap-2 max-w-2xl" data-testid="receipt-counters">
        <div className="bg-white border-2 border-zinc-900 p-3 text-center">
          <p className="font-['Chivo'] font-black text-xl md:text-2xl">{fmtKg2(receipt.kg_total)} <span className="text-sm">kg</span></p>
          <p className="text-[10px] uppercase font-bold text-zinc-500">Caricati · {receipt.pieces_count} pezzi</p>
        </div>
        <div className="bg-white border border-zinc-300 p-3 text-center">
          <p className="font-['Chivo'] font-black text-xl md:text-2xl text-zinc-700">{fmtKg2(receipt.kg_out)} <span className="text-sm">kg</span></p>
          <p className="text-[10px] uppercase font-bold text-zinc-500">Scaricati</p>
        </div>
        <div className={`p-3 text-center border-2 ${receipt.kg_residuo <= 0.0005 && receipt.pieces_count > 0 ? "bg-green-50 border-green-600" : "bg-orange-50 border-[#FF4500]"}`}>
          <p className={`font-['Chivo'] font-black text-xl md:text-2xl ${receipt.kg_residuo <= 0.0005 && receipt.pieces_count > 0 ? "text-green-700" : "text-[#E03C00]"}`} data-testid="receipt-residuo">{fmtKg2(receipt.kg_residuo)} <span className="text-sm">kg</span></p>
          <p className="text-[10px] uppercase font-bold text-zinc-500">Da scaricare</p>
        </div>
      </div>

      {receipt.lots.map((lot) => {
        const open = openLots[lot.id];
        const diff = lot.expected_kg != null ? +(lot.kg_total - lot.expected_kg).toFixed(3) : null;
        return (
          <WCard key={lot.id} className="border-2 border-zinc-300" data-testid={`lot-card-${lot.internal_lot}`}>
            <div className="p-4 flex flex-wrap items-center gap-3 justify-between">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="font-bold text-zinc-900">{lot.product_name}</p>
                  <WBadge tone="dark">{lot.category_name || "—"}</WBadge>
                  <WBadge>{lot.packaging}</WBadge>
                  {lot.brand_default && <WBadge tone="orange">{lot.brand_default}</WBadge>}
                </div>
                <p className="text-sm text-zinc-500 mt-1">
                  {lot.product_code} · Lotto forn. <b>{lot.lot_supplier || "—"}</b> · interno <b>{lot.internal_lot}</b>
                  {lot.expiry_date && <> · Scad. <b>{fmtDate(lot.expiry_date)}</b></>}
                  {lot.purchase_price != null && <> · {fmtEur(lot.purchase_price)}/{lot.price_um}</>}
                  {(lot.photos || []).length > 0 && <> · 📎 {lot.photos.length} foto</>}
                </p>
                {lot.supplier_description && <p className="text-xs italic text-zinc-400 mt-0.5">Dicitura fornitore: "{lot.supplier_description}"</p>}
                <p className="text-sm font-semibold mt-1">
                  Pesato: <span className="font-['Chivo'] font-black">{fmtKg(lot.kg_total)} kg</span> · {lot.pieces_count} pezzi
                  {lot.expected_kg != null && (
                    <span className={`ml-2 ${Math.abs(diff) > 0.005 ? "text-amber-700" : "text-green-700"}`}>
                      (bolla {fmtKg2(lot.expected_kg)} kg → {diff > 0 ? "+" : ""}{fmtKg(diff)})
                    </span>
                  )}
                </p>
              </div>
              <div className="flex gap-2 flex-wrap">
                <WButton variant="primary" className="h-14" onClick={() => navigate(`/wms/pesata/${lot.id}`)} data-testid={`weigh-btn-${lot.internal_lot}`}>
                  <Scale className="w-5 h-5" /> Pesata
                </WButton>
                <WButton className="h-14" onClick={() => openWmsPdf(`/wms/labels/lot/${lot.id}.pdf`)} disabled={!lot.pieces_count} data-testid={`lot-labels-btn-${lot.internal_lot}`}>
                  <Printer className="w-5 h-5" /> Etichette
                </WButton>
                <WButton className="h-14 !px-3" onClick={() => openMovs(lot)} data-testid={`lot-movs-${lot.internal_lot}`}>
                  <ListTree className="w-5 h-5" />
                </WButton>
                <WButton className="h-14 !px-3" onClick={() => setOpenLots({ ...openLots, [lot.id]: !open })} data-testid={`lot-toggle-${lot.internal_lot}`}>
                  {open ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                </WButton>
                {!lot.pieces_count && (
                  <WButton variant="danger" className="h-14 !px-3" onClick={() => deleteLot(lot.id)} data-testid={`lot-delete-${lot.internal_lot}`}><Trash2 className="w-5 h-5" /></WButton>
                )}
              </div>
            </div>
            {open && lot.pieces.length > 0 && (
              <div className="border-t border-zinc-200 divide-y divide-zinc-100">
                {lot.pieces.map((p) => (
                  <div key={p.id} className={`px-4 py-2 flex items-center gap-3 text-sm ${p.status !== "in_stock" ? "opacity-50" : ""}`} data-testid={`piece-row-${p.code}`}>
                    <span className="font-mono text-zinc-400 w-8">#{p.number}</span>
                    <span className="font-['Chivo'] font-black text-base w-28">{fmtKg(p.weight_kg)} kg</span>
                    {(p.units || 1) > 1 && <span className="text-zinc-600">{p.units} pz</span>}
                    <span className="flex-1 truncate">{p.brand && <WBadge tone="orange">{p.brand}</WBadge>} <span className="font-mono text-xs text-zinc-400 ml-2">{p.code}</span></span>
                    {p.status !== "in_stock" && <span className="text-xs text-zinc-500"><WBadge tone="red">{p.out_reason || "out"}</WBadge> {fmtDate(p.out_at)}</span>}
                    <button className="h-10 px-3 border border-zinc-300 font-bold text-xs uppercase hover:border-zinc-900" onClick={() => setScanPiece({ piece: p, lot, receipt })} data-testid={`piece-open-${p.code}`}>Apri</button>
                    <button className="h-10 px-3 border border-zinc-300 hover:border-zinc-900" onClick={() => openWmsPdf(`/wms/labels/piece/${p.id}.pdf`)} data-testid={`piece-print-${p.code}`}><Printer className="w-4 h-4" /></button>
                  </div>
                ))}
              </div>
            )}
          </WCard>
        );
      })}
      {receipt.lots.length === 0 && (
        <WCard className="p-8 text-center text-zinc-500" data-testid="lots-empty">Nessuna riga articolo. Premi "Articolo" per aggiungere la prima riga della bolla.</WCard>
      )}

      {/* FORM RIGA ARTICOLO */}
      <WModal open={showLotForm} onClose={() => setShowLotForm(false)} title="Aggiungi articolo al carico" wide>
        <div className="space-y-4">
          <WField label="Articolo (cerca per codice, nome o categoria)">
            {selectedProduct ? (
              <div className="flex items-center gap-3 bg-white border-2 border-green-600 px-4 h-14">
                <span className="font-bold flex-1 truncate">{selectedProduct.name}</span>
                <WBadge tone="dark">{selectedProduct.category_name || "—"}</WBadge>
                <button className="text-red-600 font-bold uppercase text-xs" onClick={() => setLotForm({ ...lotForm, product_id: "" })} data-testid="product-clear">Cambia</button>
              </div>
            ) : (
              <>
                <WInput value={prodSearch} onChange={(e) => setProdSearch(e.target.value)} placeholder="es. lombata, BOV-LOM..." data-testid="product-search" autoFocus />
                {products.length > 0 && (
                  <div className="border-2 border-zinc-300 border-t-0 bg-white max-h-72 overflow-y-auto">
                    {products.map((p) => (
                      <button key={p.id} className="w-full text-left px-4 py-3 hover:bg-orange-50 flex items-center gap-2 border-b border-zinc-100" onClick={() => setLotForm({ ...lotForm, product_id: p.id, um: p.unit === "pz" ? "pz" : "kg" })} data-testid={`product-option-${p.code}`}>
                        <span className="font-mono text-xs text-zinc-400 w-32 shrink-0">{p.code}</span>
                        <span className="font-semibold flex-1 truncate">{p.name}</span>
                        <WBadge tone="dark">{p.category_name || "—"}</WBadge>
                      </button>
                    ))}
                  </div>
                )}
              </>
            )}
          </WField>

          <div className="grid grid-cols-2 gap-3">
            <WField label="Tipo merce"><WToggle options={[{ value: "appeso", label: "Appeso" }, { value: "cartone", label: "Cartone" }]} value={lotForm.packaging} onChange={(v) => setLotForm({ ...lotForm, packaging: v })} testIdPrefix="packaging" /></WField>
            <WField label="Unità di misura"><WToggle options={[{ value: "kg", label: "KG" }, { value: "pz", label: "PZ" }]} value={lotForm.um} onChange={(v) => setLotForm({ ...lotForm, um: v })} testIdPrefix="um" /></WField>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <WField label="Lotto fornitore"><WInput value={lotForm.lot_supplier} onChange={(e) => setLotForm({ ...lotForm, lot_supplier: e.target.value })} placeholder="es. IT2026/458" data-testid="lot-supplier-input" /></WField>
            <WField label="Scadenza"><WInput type="date" value={lotForm.expiry_date} onChange={(e) => setLotForm({ ...lotForm, expiry_date: e.target.value })} data-testid="lot-expiry-input" /></WField>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <WField label="Prezzo acquisto €"><WInput type="number" step="0.01" inputMode="decimal" value={lotForm.purchase_price} onChange={(e) => setLotForm({ ...lotForm, purchase_price: e.target.value })} placeholder="0,00" data-testid="lot-price-input" /></WField>
            <WField label="Prezzo per"><WToggle options={[{ value: "kg", label: "€/KG" }, { value: "pz", label: "€/PZ" }]} value={lotForm.price_um} onChange={(v) => setLotForm({ ...lotForm, price_um: v })} testIdPrefix="priceum" /></WField>
            <WField label="Cod. articolo fornitore"><WInput value={lotForm.supplier_product_code} onChange={(e) => setLotForm({ ...lotForm, supplier_product_code: e.target.value })} placeholder="opz." data-testid="lot-supcode-input" /></WField>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <WField label="Kg da bolla (attesi)"><WInput type="number" step="0.01" inputMode="decimal" value={lotForm.expected_kg} onChange={(e) => setLotForm({ ...lotForm, expected_kg: e.target.value })} placeholder="opz." data-testid="lot-expkg-input" /></WField>
            <WField label="Colli/pezzi attesi"><WInput type="number" step="1" inputMode="numeric" value={lotForm.expected_units} onChange={(e) => setLotForm({ ...lotForm, expected_units: e.target.value })} placeholder="opz." data-testid="lot-expunits-input" /></WField>
            <WField label="Brand di default">
              <WInput list="wms-brands" value={lotForm.brand_default} onChange={(e) => setLotForm({ ...lotForm, brand_default: e.target.value })} placeholder="es. Prussian" data-testid="lot-brand-input" />
              <datalist id="wms-brands">{(boot?.brands || []).map((b) => <option key={b} value={b} />)}</datalist>
            </WField>
          </div>

          <button className="w-full h-12 border-2 border-dashed border-zinc-400 font-bold uppercase text-sm text-zinc-600 hover:border-zinc-900" onClick={() => setShowOrigin(!showOrigin)} data-testid="origin-toggle">
            {showOrigin ? "− Nascondi" : "+ Tracciabilità / origine"} (nato, allevato, macellato, sezionato)
          </button>
          {lotForm.product_id && presets.length > 0 && (
            <div data-testid="origin-presets">
              <p className="text-[10px] uppercase font-bold text-zinc-400 mb-1">Provenienze abituali di questo articolo (tap per compilare)</p>
              <div className="flex flex-wrap gap-1.5">
                {presets.map((p, i) => <PresetChip key={i} p={p} onClick={() => applyPreset(p)} />)}
              </div>
            </div>
          )}
          <WButton variant="dark" className="w-full !h-12" onClick={() => setLabelScanOpen(true)} data-testid="lot-scan-label-btn">
            <ScanText className="w-5 h-5" /> Leggi etichetta fornitore (AI) {lotForm.photos?.length > 0 && `· ${lotForm.photos.length} foto collegate`}
          </WButton>
          {showOrigin && (
            <div className="grid grid-cols-2 gap-3">
              <WField label="Nato in"><WInput value={lotForm.origin.birth} onChange={(e) => setLotForm({ ...lotForm, origin: { ...lotForm.origin, birth: e.target.value } })} placeholder="es. POLONIA" data-testid="origin-birth" /></WField>
              <WField label="Allevato in"><WInput value={lotForm.origin.rearing} onChange={(e) => setLotForm({ ...lotForm, origin: { ...lotForm.origin, rearing: e.target.value } })} placeholder="es. POLONIA" data-testid="origin-rearing" /></WField>
              <WField label="Macellato in"><WInput value={lotForm.origin.slaughter} onChange={(e) => setLotForm({ ...lotForm, origin: { ...lotForm.origin, slaughter: e.target.value } })} placeholder="es. POLONIA" data-testid="origin-slaughter" /></WField>
              <WField label="Bollo macello (CE)"><WInput value={lotForm.origin.slaughter_code} onChange={(e) => setLotForm({ ...lotForm, origin: { ...lotForm.origin, slaughter_code: e.target.value } })} placeholder="es. PL 12345 WE" data-testid="origin-slaughter-code" /></WField>
              <WField label="Sezionato in"><WInput value={lotForm.origin.cutting} onChange={(e) => setLotForm({ ...lotForm, origin: { ...lotForm.origin, cutting: e.target.value } })} placeholder="es. POLONIA" data-testid="origin-cutting" /></WField>
              <WField label="Bollo sezionamento (CE)"><WInput value={lotForm.origin.cutting_code} onChange={(e) => setLotForm({ ...lotForm, origin: { ...lotForm.origin, cutting_code: e.target.value } })} placeholder="es. PL 67890 WE" data-testid="origin-cutting-code" /></WField>
            </div>
          )}

          <WButton variant="primary" className="w-full" onClick={saveLot} data-testid="lot-save-btn">Aggiungi riga e prosegui</WButton>
        </div>
      </WModal>

      {scanPiece && (
        <WmsPieceCard data={scanPiece} onClose={() => setScanPiece(null)} onChanged={() => { setScanPiece(null); load(); }} />
      )}

      <WmsCamera open={labelScanOpen} onClose={() => setLabelScanOpen(false)} onDone={analyzeLabel} title="Foto all'etichetta del fornitore" />

      <WModal open={!!movLot} onClose={() => setMovLot(null)} title={`Movimenti lotto ${movLot?.internal_lot || ""} — dove è andato a finire`} wide>
        <div className="space-y-1" data-testid="lot-movements">
          {movs.length === 0 && <p className="text-sm text-zinc-500">Nessun movimento.</p>}
          {movs.map((m) => (
            <div key={m.id} className="flex items-center gap-3 bg-white border border-zinc-200 px-3 h-12 text-sm flex-wrap">
              <WBadge tone={m.type === "in" ? "green" : m.type === "out" ? "red" : "zinc"}>{m.type === "in" ? "ENTRATA" : m.type === "out" ? "USCITA" : "RETTIFICA"}</WBadge>
              <span className="font-['Chivo'] font-black">{m.kg > 0 && m.type !== "in" ? "" : ""}{fmtKg(m.kg)} kg</span>
              <span className="font-semibold uppercase text-xs">{m.reason}</span>
              {m.piece_code && <span className="font-mono text-xs text-zinc-400">{m.piece_code}</span>}
              <span className="flex-1" />
              <span className="text-xs text-zinc-400">{fmtDateTime(m.ts)} · {m.user_name}</span>
            </div>
          ))}
        </div>
      </WModal>
    </div>
  );
}
